import React, { useEffect, useRef, useState } from "react";
import { ClassContentDiscussionResponse } from "../../services/api.response.service";
import { Card, Dropdown, DropdownButton, Spinner } from "react-bootstrap";
import { StateKeys } from "../../redux/states/main.state";
import { IRootState } from "../../redux/states";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../services/app.service";
import ApiEndpoint from "../../services/api.endpoint.service";
import Main from "../../redux/actions/main.action";
import NoData from "../../pages/main/NoData";
import DiscussionReplies from "./DiscussionReplies";
import moment from "moment";
import { ReactComponent as ChatSVG } from "../../assets/svg/chat.svg";
import Api from "../../services/api.service";
import { toInteger } from "lodash";

interface props {
  discussions: ClassContentDiscussionResponse[] | [];
  contentID: string | number;
  /** True when the content has at least one discussion (before filtering); used to show the correct empty state */
  hasAnyDiscussions?: boolean;
  /** Called after a new post is created successfully (e.g. refetch list in parent) */
  onPostSuccess?: () => void;
}

const Discussions = ({
  discussions,
  contentID,
  hasAnyDiscussions = true,
  onPostSuccess,
}: props) => {
  const { user } = useSelector((state: IRootState) => state.setting);
  const dispatch = useDispatch();
  const pageState = useSelector(
    (state: IRootState) =>
      state.main[StateKeys.CLASS_CONTENT_DISCUSSION].pageState,
  );

  const [isPosting, setIsPosting] = useState(false);
  /** Ensures we ignore stale pageState (e.g. ERROR) until this post's saga sets LOADING */
  const sawLoadingForPostRef = useRef(false);

  const [discussion, setDiscussion] =
    useState<ClassContentDiscussionResponse>();
  const [post, setPost] = useState<string>("");
  const [discussPosts, setDiscussPosts] = useState<
    ClassContentDiscussionResponse[] | []
  >([]);
  const [discussComments, setDiscussComments] = useState<
    ClassContentDiscussionResponse[] | []
  >([]);

  const getDiscussPosts = async (classId: string | number) => {
    const resObj = await Api.getDiscussPosts(toInteger(classId));
    if (resObj) {
      // console.log("contents",resObj.data.data);
      // setDiscussPosts(resObj.data.data);
      return resObj.data;
    }
    return null;
  };

  const getDiscussComments = async (postId: string | number) => {
    const resObj = await Api.getDiscussComments(toInteger(postId));
    if (resObj) {
      // console.log("contents",resObj.data.data);
      // setDiscussComments(resObj.data.data);
      return resObj.data;
    }
    return null;
  };

  useEffect(() => {
    if (!isPosting) return;
    if (pageState === AppState.LOADING) {
      sawLoadingForPostRef.current = true;
      return;
    }
    if (!sawLoadingForPostRef.current) return;
    if (
      pageState !== AppState.LOADED &&
      pageState !== AppState.ERROR &&
      pageState !== AppState.ERROR_404
    ) {
      return;
    }
    sawLoadingForPostRef.current = false;
    if (pageState === AppState.LOADED) {
      onPostSuccess?.();
      setPost("");
    }
    setIsPosting(false);
  }, [pageState, isPosting, onPostSuccess]);

  const submit = (e: any) => {
    e.preventDefault();

    if (post!.length <= 0 || isPosting) {
      return;
    }

    sawLoadingForPostRef.current = false;
    setIsPosting(true);
    dispatch(
      Main.postPageData({
        stateKey: StateKeys.CLASS_CONTENT_DISCUSSION,
        apiEndpoint: ApiEndpoint.CONTENT_DISCUSSION(Number(contentID)),
        req: { post },
        append: "start",
      }),
    );
  };

  const conditions: string[] = ["minutes", "minute"];

  const showEmptyFilterState = hasAnyDiscussions && discussions.length === 0;

  const deleteComment = (id: any): void | undefined => {
    // e.preventDefault();
    dispatch(
      Main.postPageData({
        stateKey: StateKeys.CLASS_CONTENT_DISCUSSION,
        apiEndpoint: ApiEndpoint.CONTENT_DISCUSSION_DELETE(Number(id)),
        req: {},
        append: "start",
        refresh: false,
      }),
    );
  };

  return (
    <>
      {hasAnyDiscussions ? (
        <div className="p-3 comment-container overflow-scroll">
          {showEmptyFilterState ? (
            <div className="py-4 text-center text-muted">
              No discussions match your search or filter.
            </div>
          ) : (
            discussions.map((discuss: ClassContentDiscussionResponse) => {
              const timeLabel = conditions.some((el) =>
                moment(new Date(discuss?.created_at))
                  .startOf("minute")
                  .fromNow()
                  .includes(el),
              )
                ? moment(new Date(discuss?.created_at))
                    .startOf("minute")
                    .fromNow()
                : moment(new Date(discuss?.created_at)).format("ll");

              return (
                <Card
                  key={discuss.id}
                  className="discuss-post-card"
                  onClick={() => setDiscussion(discuss)}
                >
                  <div className="discuss-post">
                    <div className="discuss-post__header">
                      <div className="discuss-post__author">
                        <img
                          className="discuss-post__avatar discuss-profile"
                          src={discuss.user.image_url}
                          alt={discuss.user.name}
                        />
                        <span className="discuss-post__name">
                          {discuss.user.name}
                        </span>
                      </div>
                      {user?.id === discuss.user.id && (
                        <DropdownButton
                          variant="transparent"
                          className="discussion-replies-modal__dropdown"
                          title={
                            <i
                              className="las la-ellipsis-v discussion-replies-modal__kebab"
                              aria-hidden
                            />
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                        >
                          <Dropdown.Item
                            eventKey="del"
                            onClick={() => deleteComment(discuss.id)}
                          >
                            Delete
                          </Dropdown.Item>
                        </DropdownButton>
                      )}
                    </div>

                    <p className="discuss-post__body">{discuss?.post}</p>

                    <div className="discuss-post__footer">
                      <button
                        type="button"
                        className="discuss-post__footer-count"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDiscussion(discuss);
                        }}
                      >
                        <i
                          className="bi bi-chat discuss-post__footer-count-icon"
                          aria-hidden
                        />
                        <span>{discuss.total_comments}</span>
                      </button>
                      <button
                        type="button"
                        className="discuss-post__footer-add"
                        onClick={(e) => {
                          e.stopPropagation();
                          setDiscussion(discuss);
                        }}
                      >
                        <ChatSVG />
                        Add a reply
                      </button>
                      <span className="discuss-post__footer-time">
                        {timeLabel}
                      </span>
                    </div>
                  </div>
                </Card>
              );
            })
          )}
        </div>
      ) : (
        <NoData title="DiscussionNotFound" />
      )}

      <div className="position-absolute bottom-0 w-100 add-comment">
        <form onSubmit={submit}>
          <div className="row  align-items-center px-4 mt-2 position-relative">
            <div className="d-flex gap-3 align-items-center col-2">
              <div
                className="profile-picture rounded-pill"
                style={{ backgroundImage: `url(${user?.image_url})` }}
              ></div>

              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                fill="currentColor"
                className="bi bi-emoji-smile"
                viewBox="0 0 16 16"
              >
                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                <path d="M4.285 9.567a.5.5 0 0 1 .683.183A3.498 3.498 0 0 0 8 11.5a3.498 3.498 0 0 0 3.032-1.75.5.5 0 1 1 .866.5A4.498 4.498 0 0 1 8 12.5a4.498 4.498 0 0 1-3.898-2.25.5.5 0 0 1 .183-.683zM7 6.5C7 7.328 6.552 8 6 8s-1-.672-1-1.5S5.448 5 6 5s1 .672 1 1.5zm4 0c0 .828-.448 1.5-1 1.5s-1-.672-1-1.5S9.448 5 10 5s1 .672 1 1.5z" />
              </svg>
            </div>

            <div className="col-9">
              <textarea
                className="form-control p-2"
                name="post"
                id="post"
                placeholder="Add a post..."
                onChange={(e) => setPost(e.target.value)}
                required
                disabled={isPosting}
                value={post}
              />
            </div>

            <div className="col-1 d-flex align-items-center justify-content-center">
              {isPosting ? (
                <Spinner
                  animation="border"
                  size="sm"
                  role="status"
                  className="ms-3"
                  style={{
                    width: 24,
                    height: 24,
                    color: "rgba(218, 55, 50, 1)",
                  }}
                />
              ) : (
                <svg
                  style={{ cursor: "pointer" }}
                  onClick={submit}
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  fill="rgba(218, 55, 50, 1)"
                  className="bi bi-send ms-3"
                  viewBox="0 0 16 16"
                  aria-hidden
                >
                  <path d="M15.854.146a.5.5 0 0 1 .11.54l-5.819 14.547a.75.75 0 0 1-1.329.124l-3.178-4.995L.643 7.184a.75.75 0 0 1 .124-1.33L15.314.037a.5.5 0 0 1 .54.11ZM6.636 10.07l2.761 4.338L14.13 2.576 6.636 10.07Zm6.787-8.201L1.591 6.602l4.339 2.76 7.494-7.493Z" />
                </svg>
              )}
            </div>
          </div>{" "}
        </form>
      </div>

      <DiscussionReplies
        setDiscussion={setDiscussion}
        discussion={discussion!}
        contentID={contentID}
      />
    </>
  );
};

export default Discussions;
